import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

const DOCK_MS = 700;
const DOCK_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

const ScheduleSelectDock = ({ children, active = false }) => {
  const slotRef = useRef(null);
  const sentinelRef = useRef(null);
  const dockRef = useRef(null);
  const firstRectRef = useRef(null);
  const [docked, setDocked] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const next = !entry.isIntersecting;
        setDocked((prev) => {
          if (prev === next) return prev;
          const dock = dockRef.current;
          const slot = slotRef.current;
          if (dock) {
            firstRectRef.current = dock.getBoundingClientRect();
            if (next && slot) {
              slot.style.setProperty(
                "--select-dock-h",
                `${Math.ceil(dock.getBoundingClientRect().height)}px`,
              );
            }
          }
          return next;
        });
      },
      { threshold: 0, rootMargin: "-8px 0px 0px 0px" },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const dock = dockRef.current;
    const first = firstRectRef.current;
    firstRectRef.current = null;
    if (!dock || !first) return undefined;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const last = dock.getBoundingClientRect();
    const dx = first.left - last.left;
    const dy = first.top - last.top;

    if (reduceMotion || (Math.abs(dx) < 2 && Math.abs(dy) < 2)) {
      if (!docked && slotRef.current) {
        slotRef.current.style.removeProperty("--select-dock-h");
      }
      return undefined;
    }

    dock.style.transition = "none";
    dock.style.transform = `translate(${dx}px, ${dy}px)`;
    dock.getBoundingClientRect();
    dock.style.transition = `transform ${DOCK_MS}ms ${DOCK_EASING}`;
    dock.style.transform = "none";

    const onEnd = (event) => {
      if (event.propertyName !== "transform") return;
      dock.style.transition = "";
      dock.style.transform = "";
      if (!docked && slotRef.current) {
        slotRef.current.style.removeProperty("--select-dock-h");
      }
      dock.removeEventListener("transitionend", onEnd);
    };
    dock.addEventListener("transitionend", onEnd);
    return () => dock.removeEventListener("transitionend", onEnd);
  }, [docked]);

  return (
    <div
      ref={slotRef}
      className={`schedule-mng-select-slot${docked ? " is-docked" : ""}`}
    >
      <div
        ref={sentinelRef}
        className="schedule-mng-select-sentinel"
        aria-hidden="true"
      />
      <div
        ref={dockRef}
        className={`schedule-mng-select-dock${docked ? " is-docked" : ""}${
          active ? " is-active" : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
};

export default ScheduleSelectDock;
